'use client';
import React from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { ISetParams } from '@benzinga/calendar-manager';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { SessionContext } from '@benzinga/session-context';
import { fetchSpac } from './Services';

export const fetchSPAC = fetchSpac;
export const SPACColumnsDefinition = ColumnsDefinition;

export const SpacCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const session = React.useContext(SessionContext);

  const dateRange: IDateRange = initialDateRange ?? {
    date_from: formatRequestDate(DateTime.now().minus({ days: 30 }).toJSDate()),
    date_to: formatRequestDate(new Date()),
  };

  const initialParams = React.useMemo(
    () => ({
      dateFrom: dateRange.date_from ?? '',
      dateTo: dateRange.date_to ?? '',
      ...(initialTicker && { symbols: initialTicker }),
    }),
    [dateRange.date_from, dateRange.date_to, initialTicker],
  );

  const { loading, onFilterChanged, onViewportChanged, setTableData, tableData } = useCalendarPagedFetch({
    fetchPage: (params, page) =>
      fetchSpac(session.getSession(), {
        ...(params as ISetParams),
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
      }),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setTableData]);

  const onTableReady = React.useCallback(() => {
    if (initialData) {
      setTableData(initialData, initialParams);
    } else {
      onFilterChanged(initialParams);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }, [initialData, initialParams, onFilterChanged, onReady, setTableData]);

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      initialDateRange={dateRange}
      leftSlot={leftSlot}
      onFilterChanged={onFilterChanged}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
      viewportChange={onViewportChanged}
    />
  );
};
